import type { EmailSettings, EmailTestResponse } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Mail, RefreshCw, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { EvolutionSettingsCard } from './settings-whatsapp.js';
import { InstagramSettingsCard } from './settings-instagram.js';
import { GoogleAdsSettingsCard } from './settings-google.js';

export const emailSettingsQuery = queryOptions({
  queryKey: ['platform', 'settings', 'email'],
  queryFn: () => api<EmailSettings>('/platform/settings/email'),
});

const DOMAIN_STATUS: Record<NonNullable<EmailSettings['domain']>['status'], { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  authenticated: { label: 'Autenticado', tone: 'success' },
  pending: { label: 'Aguardando DNS', tone: 'warning' },
  not_added: { label: 'Não cadastrado na Brevo', tone: 'danger' },
  unknown: { label: 'Não foi possível consultar', tone: 'neutral' },
};
const RECORD_LABEL: Record<string, string> = {
  brevo_code: 'Código de verificação', dkim_record: 'DKIM', dkim1Record: 'DKIM 1', dkim2Record: 'DKIM 2', dmarc_record: 'DMARC',
};
const when = (iso: string) => new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button variant="ghost" size="icon" aria-label={`Copiar ${label}`}
      onClick={() => { void navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
    </Button>
  );
}

/** Console → Configurações → E-mail: chave da Brevo, remetente, teste e situação do domínio. */
export function EmailSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, refetch, isFetching } = useQuery(emailSettingsQuery);
  const [apiKey, setApiKey] = useState('');
  const [from, setFrom] = useState('nao-responda@domnexus.com.br');
  useEffect(() => { if (data?.from) setFrom(data.from); }, [data?.from]);

  const save = useMutation({
    mutationFn: () => api<EmailSettings>('/platform/settings/email', { method: 'PUT', body: { from, ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}) } }),
    onSuccess: (res) => { qc.setQueryData(emailSettingsQuery.queryKey, res); setApiKey(''); test.reset(); },
  });
  const test = useMutation({ mutationFn: () => api<EmailTestResponse>('/platform/settings/email/test', { method: 'POST' }) });

  if (isLoading || !data) return <div className="grid h-60 place-items-center"><Spinner className="size-6 text-fg-subtle" label="Carregando" /></div>;
  const domain = data.domain;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight text-fg">Configurações</h1>
      <p className="mt-1 text-sm text-fg-muted">Valem para todas as clínicas da plataforma.</p>

      <Card className="mt-6 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-fg"><Mail className="size-4 text-fg-subtle" />E-mail transacional (Brevo)</h2>
            <p className="mt-1 text-[13px] text-fg-muted">Usado para enviar o link de acesso à área do paciente. O remetente aparece com o nome da clínica.</p>
          </div>
          {data.configured ? <Badge tone="success" className="shrink-0">Configurado</Badge> : <Badge tone="warning" className="shrink-0 whitespace-nowrap">Não configurado</Badge>}
        </div>

        {data.configured && (
          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 rounded-lg bg-surface-muted p-3.5 text-[13px]">
            <dt className="text-fg-muted">Chave</dt><dd className="font-mono text-fg">{data.keyHint}</dd>
            {data.account && <><dt className="text-fg-muted">Conta Brevo</dt><dd className="text-fg">{data.account}</dd></>}
            <dt className="text-fg-muted">Origem</dt>
            <dd className="text-fg">{data.source === 'console' ? `Console${data.updatedAt ? ` · ${when(data.updatedAt)}${data.updatedBy ? ` por ${data.updatedBy}` : ''}` : ''}` : 'Variável de ambiente do servidor'}</dd>
          </dl>
        )}

        <form className="mt-5 space-y-4" noValidate onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <Field label="Chave de API"
            hint="Na Brevo: seu nome → SMTP e API → aba Chaves de API → Gerar nova chave. Começa com xkeysib- (não é a senha SMTP).">
            <Input type="password" autoComplete="off" spellCheck={false} value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={data.configured ? 'Deixe em branco para manter a atual' : 'xkeysib-…'} className="font-mono" />
          </Field>
          <Field label="Remetente" hint="O domínio deste e-mail precisa estar autenticado na Brevo.">
            <Input type="email" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          {save.error && <Alert tone="danger">{save.error.message}</Alert>}
          {save.isSuccess && <Alert tone="success">Configuração salva. A chave foi conferida com a Brevo.</Alert>}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={save.isPending} disabled={!data.configured && !apiKey.trim()}>Salvar</Button>
            {data.configured && (
              <Button variant="secondary" loading={test.isPending} onClick={() => test.mutate()}><Send className="size-4" />Enviar e-mail de teste</Button>
            )}
          </div>
          {test.data && <Alert tone="success">E-mail de teste enviado para {test.data.sentTo}. Confira a caixa de entrada e o spam.</Alert>}
          {test.error && <Alert tone="danger">{test.error.message}</Alert>}
        </form>
      </Card>

      {domain && (
        <Card className="mt-4 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-fg">Domínio do remetente: {domain.name}</h2>
              <p className="mt-1 text-[13px] text-fg-muted">
                {domain.status === 'authenticated' && 'Pronto: os e-mails saem assinados (SPF/DKIM) e não caem no spam.'}
                {domain.status === 'pending' && 'Crie os registros abaixo no DNS do domínio (Hostinger) e depois clique em "Autenticar" na Brevo.'}
                {domain.status === 'not_added' && 'Na Brevo: Remetentes, domínios e IPs → Domínios → Adicionar domínio. Depois volte aqui para ver os registros.'}
                {domain.status === 'unknown' && 'A Brevo não respondeu agora. Tente verificar de novo.'}
              </p>
            </div>
            <Badge tone={DOMAIN_STATUS[domain.status].tone} className="shrink-0 whitespace-nowrap">{DOMAIN_STATUS[domain.status].label}</Badge>
          </div>
          {domain.records.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-[12.5px]">
                <thead className="text-fg-muted"><tr><th className="py-1.5 pr-3 font-medium">Registro</th><th className="pr-3 font-medium">Tipo</th><th className="pr-3 font-medium">Nome</th><th className="font-medium">Valor</th><th /></tr></thead>
                <tbody>
                  {domain.records.map((r) => (
                    <tr key={r.name} className="border-t border-border align-top">
                      <td className="py-2 pr-3 text-fg">{RECORD_LABEL[r.name] ?? r.name}</td>
                      <td className="py-2 pr-3 font-mono">{r.type}</td>
                      <td className="py-2 pr-3 font-mono break-all">{r.host}</td>
                      <td className="py-2 font-mono break-all text-fg"><span className="flex items-start gap-1">{r.value}<CopyButton value={r.value} label={RECORD_LABEL[r.name] ?? r.name} /></span></td>
                      <td className="py-2 pl-2">{r.ok ? <Badge tone="success">OK</Badge> : <Badge tone="warning">Pendente</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Button variant="secondary" size="sm" className="mt-4" loading={isFetching} onClick={() => void refetch()}>
            <RefreshCw className="size-4" />Verificar de novo
          </Button>
        </Card>
      )}
      <EvolutionSettingsCard />
      <InstagramSettingsCard />
      <GoogleAdsSettingsCard />
    </div>
  );
}
